import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogOut, User, ShieldCheck, KeyRound, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };
  const active = user?.active_role || (user?.roles || [])[0];
  const status = user?.status || "active";

  const [mfa, setMfa] = useState(null);
  const [cur, setCur] = useState(""); const [np, setNp] = useState(""); const [np2, setNp2] = useState("");
  const [pwBusy, setPwBusy] = useState(false); const [pwErr, setPwErr] = useState("");
  const [disPw, setDisPw] = useState(""); const [disBusy, setDisBusy] = useState(false);

  const loadMfa = () => api.get("/auth/mfa/status").then((r) => setMfa(r.data)).catch(() => {});
  useEffect(() => { loadMfa(); }, []);

  const changePw = async (e) => {
    e.preventDefault(); setPwErr("");
    if (np !== np2) { setPwErr("Les mots de passe ne correspondent pas."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: np });
      toast.success("Mot de passe modifié.");
      setCur(""); setNp(""); setNp2("");
    } catch (err) { setPwErr(apiErr(err.response?.data?.detail) || "Erreur"); }
    finally { setPwBusy(false); }
  };

  const disableMfa = async () => {
    setDisBusy(true);
    try {
      await api.post("/auth/mfa/disable", { current_password: disPw, new_password: "x" });
      toast.success("Double authentification désactivée.");
      setDisPw(""); await loadMfa(); await refreshUser();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Erreur"); }
    finally { setDisBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-display font-bold text-slate-900">{t("profile")}</h1>

      <Card className="card-float border-slate-200 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-14 w-14 rounded-full bg-primary text-white flex items-center justify-center"><User className="h-7 w-7" /></div>
          <div>
            <div className="font-display font-semibold text-lg text-slate-900" data-testid="profile-name">{user?.name}</div>
            <div className="text-sm text-slate-500">{user?.email}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div><div className="text-slate-500">{t("activeSpace")}</div><Badge className="mt-1 bg-accent text-accent-foreground border-0">{t(`role_${active}`)}</Badge></div>
          <div><div className="text-slate-500">{t("status")}</div><Badge className="mt-1" variant="secondary" data-testid="profile-status">{t(`ust_${status}`) !== `ust_${status}` ? t(`ust_${status}`) : status}</Badge></div>
          <div className="sm:col-span-2"><div className="text-slate-500 mb-1">{t("roles")}</div>
            <div className="flex flex-wrap gap-1">{(user?.roles || []).map((r) => <Badge key={r} variant="secondary">{t(`role_${r}`)}</Badge>)}</div>
          </div>
        </div>
        <Button data-testid="profile-logout" onClick={doLogout} variant="outline" className="mt-6 gap-2 text-red-600"><LogOut className="h-4 w-4" />{t("logout")}</Button>
      </Card>

      {/* Sécurité — double authentification */}
      <Card className="card-float border-slate-200 p-6" data-testid="profile-mfa-card">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg text-slate-900">Double authentification (2FA)</h2></div>
        {!mfa ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Badge className={mfa.mfa_enabled ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-600 border-0"} data-testid="mfa-state">
                {mfa.mfa_enabled ? "Activée" : "Désactivée"}
              </Badge>
              {mfa.mfa_required && <Badge variant="secondary">Obligatoire pour votre rôle</Badge>}
              {mfa.mfa_enabled && <span className="text-xs text-slate-500">{mfa.recovery_codes_remaining} codes de récupération restants</span>}
            </div>
            {!mfa.mfa_enabled ? (
              <Button data-testid="mfa-activate" onClick={() => navigate("/mfa-setup")} className="rounded-full">Activer la 2FA</Button>
            ) : mfa.mfa_required ? (
              <p className="text-sm text-slate-500">La 2FA est obligatoire pour votre rôle et ne peut pas être désactivée.</p>
            ) : (
              <div className="flex items-end gap-2">
                <div className="grid gap-1.5 flex-1">
                  <label className="text-xs text-slate-500">Mot de passe actuel pour désactiver</label>
                  <Input data-testid="mfa-disable-pw" type="password" value={disPw} onChange={(e) => setDisPw(e.target.value)} placeholder="••••••••" />
                </div>
                <Button data-testid="mfa-disable-btn" variant="outline" className="text-red-600" disabled={disBusy || !disPw} onClick={disableMfa}>
                  {disBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Désactiver"}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Changement de mot de passe */}
      <Card className="card-float border-slate-200 p-6" data-testid="profile-password-card">
        <div className="flex items-center gap-2 mb-4"><KeyRound className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg text-slate-900">Changer le mot de passe</h2></div>
        <form onSubmit={changePw} className="space-y-3 max-w-sm">
          <div className="grid gap-1.5">
            <label className="text-sm text-slate-600">Mot de passe actuel</label>
            <Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required placeholder="••••••••" />
          </div>
          <div className="grid gap-1.5">
            <label className="text-sm text-slate-600">Nouveau mot de passe</label>
            <Input data-testid="cp-new" type="password" value={np} onChange={(e) => setNp(e.target.value)} required placeholder="••••••••" />
          </div>
          <div className="grid gap-1.5">
            <label className="text-sm text-slate-600">Confirmer</label>
            <Input data-testid="cp-confirm" type="password" value={np2} onChange={(e) => setNp2(e.target.value)} required placeholder="••••••••" />
          </div>
          <p className="text-xs text-slate-400">Au moins 12 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.</p>
          {pwErr && <div data-testid="cp-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{pwErr}</div>}
          <Button data-testid="cp-submit" type="submit" disabled={pwBusy} className="rounded-full">
            {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mettre à jour"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
